/**
 * LandlineConfirmationModal Component
 * SESSION_211: Modal to confirm if pre-filled phone number is mobile
 *
 * Shown when user has a phone number on file (from their account or business)
 * and SMS notifications are being enabled. Asks if the phone is mobile since
 * landlines and VoIP numbers cannot receive SMS.
 */

import React, { useCallback, useMemo } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Alert
} from '@mantine/core';
import { IconPhone, IconCheck, IconX } from '@tabler/icons-react';
import type { LandlineConfirmationModalProps } from './LandlineConfirmationModal.types';
import { formatPhoneForDisplay } from '../../../../utils/phoneValidation';

/**
 * LandlineConfirmationModal - Confirmation dialog for mobile vs landline
 */
export const LandlineConfirmationModal: React.FC<LandlineConfirmationModalProps> = ({
  opened,
  onClose,
  onConfirmMobile,
  onNotMobile,
  phoneNumber,
  phoneSource,
  isLoading = false
}): React.ReactElement => {
  /**
   * Get message text based on phone source
   */
  const getMessage = useMemo((): string => {
    const formattedPhone = formatPhoneForDisplay(phoneNumber) || phoneNumber;
    if (phoneSource === 'user') {
      return `We have ${formattedPhone} on file for your account. Is this a mobile number that can receive SMS text messages?`;
    }
    return `We have ${formattedPhone} on file for your business. Is this a mobile number that can receive SMS text messages?`;
  }, [phoneNumber, phoneSource]);

  /**
   * Handle confirm mobile button click
   */
  const handleConfirmMobile = useCallback((): void => {
    onConfirmMobile();
  }, [onConfirmMobile]);

  /**
   * Handle not mobile button click
   */
  const handleNotMobile = useCallback((): void => {
    onNotMobile();
  }, [onNotMobile]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconPhone size={24} color="var(--mantine-color-blue-6)" />
          <Text fw={600} size="lg">Confirm Mobile Number</Text>
        </Group>
      }
      centered
      size="md"
    >
      <Stack gap="lg">
        {/* Phone Number Alert */}
        <Alert icon={<IconPhone size={16} />} color="blue" variant="light">
          {getMessage}
        </Alert>

        {/* Info Text */}
        <Text size="sm" c="dimmed">
          SMS notifications are only available for mobile phones. Landlines and VoIP
          numbers may not be able to receive text messages.
        </Text>

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            color="gray"
            onClick={handleNotMobile}
            disabled={isLoading}
            leftSection={<IconX size={16} />}
          >
            No, it&apos;s not mobile
          </Button>
          <Button
            onClick={handleConfirmMobile}
            loading={isLoading}
            leftSection={<IconCheck size={16} />}
          >
            Yes, it&apos;s mobile
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
